import { Accordion, ActionIcon, Alert, Box, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconSearch, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';

import { InFlightTable } from './components/InFlightTable';
import { TaskAccordionItem } from './components/TaskAccordionItem';
import { useArenaCapability, useArenaTasks } from './hooks/useModelArena';

export default function ModelArenaTab() {
  const { tasks, isLoading } = useArenaTasks();
  const { capability } = useArenaCapability();
  const [query, setQuery] = useState('');
  const [opened, setOpened] = useState<string[] | null>(null);
  const [failure, setFailure] = useState<{ title: string; message: string } | null>(null);

  if (isLoading) {
    return <PageLoader />;
  }

  const reportError = (title: string, message: string) => setFailure({ title, message });

  const taskKeys = (tasks ?? []).map(task => task.taskKey);
  // A search reaches across tasks, so every task opens while one is active; otherwise the reader's
  // own choice stands, defaulting to the first task open.
  const openItems = query.trim() ? taskKeys : (opened ?? taskKeys.slice(0, 1));

  return (
    <Stack gap="lg">
      <Box>
        <Text fw={600} size="lg">
          Model arena
        </Text>
        <Text size="sm" c="dimmed">
          Compare candidate models for an evaluation task before adding one to its pool. Each
          candidate runs the real pipeline over the same fixtures, pinned so its results are its
          own, and is scored against gold. Runs are queued and picked up by a worker, so they can be
          scheduled away from busy hours.
        </Text>
      </Box>

      {failure && (
        <Alert
          color="red"
          icon={<IconAlertTriangle size={16} />}
          title={failure.title}
          withCloseButton
          onClose={() => setFailure(null)}
        >
          {failure.message}
        </Alert>
      )}

      {capability?.mayRun === false && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Viewing only">
          {capability.reason}
        </Alert>
      )}

      <InFlightTable reportError={reportError} />

      <TextInput
        placeholder="Search runs by model, status, score or day"
        value={query}
        onChange={event => setQuery(event.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={
          query && (
            <ActionIcon
              variant="subtle"
              c="dimmed"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <IconX size={14} />
            </ActionIcon>
          )
        }
      />

      {taskKeys.length === 0 && (
        <Text size="sm" c="dimmed">
          No evaluation tasks are registered.
        </Text>
      )}

      <Accordion variant="separated" multiple value={openItems} onChange={setOpened}>
        {(tasks ?? []).map(task => (
          <TaskAccordionItem
            key={task.taskKey}
            task={task}
            isOpen={openItems.includes(task.taskKey)}
            query={query}
            reportError={reportError}
          />
        ))}
      </Accordion>
    </Stack>
  );
}
